import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import DatePickerNative from '@/components/UI/DatePickerNative';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import { Select, Textarea } from '@/components/UI/FormFields/Latest';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { TeamGlossary } from '@/model/Glosary.model';
import type { State } from '@/model/State.model';
import { ApiService } from '@/support/ApiService';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { RouteService } from '@/support/RouteService';
import { DateUtils } from '@/utils/Date.utils';
import { createSelector } from '@reduxjs/toolkit';
import { type FC, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

interface EuthaniseSubjectEditingModalProps {
  glossary_id: number;
  terminated_at: string;
  disease_survivor: boolean;
  termination_value: string;
}

interface EuthanizeReasonOption {
  label: string;
  value: number;
}

interface EuthaniseSubjectProps {
  subjects: Array<Animal>;
  handleCallback: (message?: string) => void;
  closeModal: () => void;
  editing?: EuthaniseSubjectEditingModalProps;
  reasons?: Array<EuthanizeReasonOption>;
  fetchError?: boolean | Error;
  consumptionEnabled?: boolean;
}

interface EuthaniseSubjectFormProps {
  glossary_id: number;
  terminated_at: string;
  disease_survivor: boolean;
  termination_value: string;
  reason_for_change?: string;
}

const featureSelector = createSelector(
  (state: State) => state?.team?.features ?? {},
  (features) => ({
    reasonForChange: features?.reason_for_change ?? false,
    diseaseSurvivorFlag: features?.disease_survivor ?? false,
  })
);

const EuthaniseSubjectForm: FC<EuthaniseSubjectProps> = ({
  subjects,
  editing,
  handleCallback,
  closeModal,
  reasons,
  fetchError,
  consumptionEnabled = false,
}) => {
  const [submissionError, setSubmissionError] = useState<boolean | Error>(false);

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      glossary_id: editing?.glossary_id ?? reasons?.[0]?.value ?? 0,
      terminated_at: editing?.terminated_at ?? DateUtils.dateNow(),
      disease_survivor: editing?.disease_survivor ?? false,
      termination_value: editing?.termination_value ?? '',
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = formMethods;

  const { reasonForChange, diseaseSurvivorFlag } = useSelector(featureSelector);

  const onSubmit = async (form: EuthaniseSubjectFormProps) => {
    const animalApiIds = subjects.map((s) => s.api_id).filter(_notNil);

    if (_isEmpty(animalApiIds) || !_notNil(form.glossary_id)) {
      return;
    }

    if (_notNil(editing)) {
      const response = await ApiService.call({
        endpoint: 'PATCH /api/v1/animals/update-terminate',
        body: {
          animals: animalApiIds.map((animalApiId) => ({
            animal_api_id: animalApiId,
            glossary_id: form.glossary_id,
            terminated_at: _isNotEmpty(form.terminated_at) ? form.terminated_at : null,
            disease_survivor: form.disease_survivor,
            termination_value: _isNotEmpty(form.termination_value) ? form.termination_value : null,
          })),
          reason_for_change: form.reason_for_change ?? null,
        },
      });
      if (response.type === 'error') {
        setSubmissionError(response.error);
        return;
      }
      handleCallback('Successfully marked as deceased');
      return;
    }

    const response = await ApiService.call({
      endpoint: 'POST /api/v1/animals/terminate',
      body: {
        animal_api_ids: animalApiIds,
        glossary_id: form.glossary_id,
        terminated_at: _isNotEmpty(form.terminated_at) ? form.terminated_at : null,
        disease_survivor: form.disease_survivor,
        termination_value: _isNotEmpty(form.termination_value) ? form.termination_value : null,
      },
    });
    if (response.type === 'error') {
      setSubmissionError(response.error);
      return;
    }
    handleCallback('Successfully marked as deceased');
  };

  const showReasonForChange = editing && reasonForChange;

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} data-test-component="EuthaniseSubjectForm" data-test-element="container">
        <div className={`${isSubmitting ? 'ui__disabled' : ''} ui__card pa3`} data-testid="euthanise-subject">
          {submissionError && (
            <ApiErrorBanner className="ml3" errorType={ApiErrorType.SUBMIT} error={submissionError} />
          )}
          {fetchError && <ApiErrorBanner className="ml3" errorType={ApiErrorType.FETCH} error={fetchError} />}
          {_isNotEmpty(subjects) && (
            <div
              className={`${isSubmitting ? 'ui__disabled' : ''} center mv3 pa4 bg-white br2 shadow-4`}
              style={{ maxWidth: '420px' }}
            >
              <h3 className="normal f4 lh-title pb3">
                Mark {subjects.length === 1 ? subjects[0].name : `${subjects.length} Animals`} as deceased
              </h3>
              {consumptionEnabled && _isNotEmpty(reasons) && (
                <Banner info className="mb3" dismiss={false}>
                  <h3 className="f6 normal lh-title pb1">
                    Marking as deceased will impact future cage calculations and survival data, historical cage
                    measurements will remain unchanged.
                  </h3>
                  <p className="f6">
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dib link blue"
                      href={RouteService.help('cage_food_fluid_calculations')}
                    >
                      Read more
                    </a>
                  </p>
                </Banner>
              )}
              {_isEmpty(reasons) ? (
                <Banner info className="mw6 mb3" dismiss={false}>
                  <h3 className="f5 normal lh-title pb2">
                    There are no deceased reasons selected in your team glossary
                  </h3>
                  <p className="lh-copy f6">Contact your team administrator to add terms to select from.</p>
                </Banner>
              ) : (
                <>
                  <label className="mb2">Deceased date</label>
                  <Controller
                    name="terminated_at"
                    control={control}
                    defaultValue={DateUtils.dateNow()}
                    render={({ field: { onChange, value } }) => (
                      <DatePickerNative
                        value={value}
                        onChange={onChange}
                        className="mb0"
                        testId="terminated_at_date_picker"
                      />
                    )}
                  />
                  <div className="mb3" data-test-element="deceased-reasons-container">
                    <Controller
                      name="glossary_id"
                      control={control}
                      rules={{ required: 'Reason is required' }}
                      render={({ field: { value } }) => (
                        <Select
                          name="glossary_id"
                          required
                          value={value}
                          label="Select a reason"
                          options={reasons}
                          disabled={isSubmitting}
                        />
                      )}
                    />
                  </div>
                  {diseaseSurvivorFlag && (
                    <div className="mb3" data-test-element="deceased-reasons-container">
                      <Controller
                        name="disease_survivor"
                        control={control}
                        render={({ field: { onChange, value } }) => {
                          return (
                            <Checkbox
                              name="disease_survivor"
                              checked={value}
                              onChange={(e) => {
                                onChange(e.target.checked);
                              }}
                              label="Survived to end of study"
                              disabled={isSubmitting}
                            />
                          );
                        }}
                      />
                    </div>
                  )}
                  <div
                    className={`${showReasonForChange ? 'mb1' : 'mb3'}`}
                    data-test-element="termination-value-container"
                  >
                    <Controller
                      name="termination_value"
                      control={control}
                      render={({ field: { value } }) => (
                        <Textarea name="termination_value" maxLength={5000} label="Additional comment" value={value} />
                      )}
                    />
                  </div>
                  {showReasonForChange && (
                    <div className="mb3" data-test-element="reason-for-change-container">
                      <ReasonForChange />
                    </div>
                  )}
                </>
              )}
              <div>
                <Button
                  disabled={isSubmitting || _isEmpty(reasons)}
                  className="mr2"
                  onClick={handleSubmit(onSubmit)}
                  testId="euthanise-subject__save"
                >
                  Save
                </Button>
                <Button plain onClick={closeModal}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export const EuthaniseSubject: FC<EuthaniseSubjectProps> = ({
  subjects,
  editing,
  handleCallback,
  closeModal,
  consumptionEnabled,
}) => {
  const { loading: euthanizeReasonsLoading, response: euthanizeReasonsResponse } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'team-glossary.list',
    query: { group: 'euthanize_reasons' },
  });

  const euthanizeReasons = (euthanizeReasonsResponse?.body as { data?: Array<TeamGlossary> })?.data ?? [];
  const euthanizeReasonsError = euthanizeReasonsResponse?.type === 'error' ? euthanizeReasonsResponse.error : false;

  const reasons: Array<EuthanizeReasonOption> = euthanizeReasons.map(({ id, title }) => ({
    label: title ?? '',
    value: Number(id),
  }));

  if (euthanizeReasonsLoading) {
    return null;
  }

  return (
    <EuthaniseSubjectForm
      subjects={subjects}
      editing={editing}
      handleCallback={handleCallback}
      closeModal={closeModal}
      reasons={reasons}
      fetchError={euthanizeReasonsError}
      consumptionEnabled={consumptionEnabled}
    />
  );
};
